/**
 * Alerts tab component.
 *
 * Displays active CloudWatch alarms.
 *
 * Implementation plan reference: D2.18, D2.18a (client-side env filter)
 */

import { useEffect, useMemo, useState } from 'react';
import { ApiError, getAlerts } from '@/api/client';
import { formatDateTime } from '@/utils/format';
import { POLL_INTERVAL_SERVICES_ALERTS_MS, useRefresh } from '@/context/RefreshContext';
import { Badge } from '@/components/Badge';
import type { AlertsResponse, CloudWatchAlarm, Environment } from '@/types/api';

type AlertEnvFilter = 'all' | Environment;

function inferAlarmEnvironment(alarm: CloudWatchAlarm): Environment | null {
  const haystack = [alarm.alarmName, ...Object.values(alarm.dimensions)].join(' ').toLowerCase();

  if (/\bprod\b|-prod|_prod|\.prod\b|cluster.*-prod|service.*-prod/.test(haystack)) {
    return 'prod';
  }
  if (/\bstaging\b|-staging|_staging|cluster.*-stage|service.*-stage|\bstage\b|-stage|_stage/.test(haystack)) {
    return 'staging';
  }
  if (/\bdev\b|-dev|_dev|\.dev\b|cluster.*-dev|service.*-dev/.test(haystack)) {
    return 'dev';
  }
  return null;
}

function severityVariant(sev: CloudWatchAlarm['severity']): 'error' | 'warning' {
  return sev === 'critical' ? 'error' : 'warning';
}

function alarmContextLabel(alarm: CloudWatchAlarm): string {
  const d = alarm.dimensions;
  const service = d.ServiceName ?? d.service ?? d.ClusterName ?? d.LoadBalancer;
  if (service !== undefined && service !== '') {
    return service;
  }
  return alarm.metricName;
}

const formatWhen = (iso: string): string =>
  formatDateTime(iso, { dateStyle: 'medium', timeStyle: 'short' });

export function AlertsTab(): JSX.Element {
  const { mode, manualRefreshNonce, tabVisible } = useRefresh();
  const [data, setData] = useState<AlertsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [envFilter, setEnvFilter] = useState<AlertEnvFilter>('all');

  useEffect(() => {
    if (!tabVisible) {
      return undefined;
    }

    let cancelled = false;

    function runFetch(silent: boolean): void {
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }

      void getAlerts()
        .then((response) => {
          if (!cancelled) {
            setData(response);
          }
        })
        .catch((err: unknown) => {
          if (cancelled || silent) {
            return;
          }
          if (err instanceof ApiError) {
            setError(`Failed to load alerts data (${err.status}).`);
          } else {
            setError('Failed to load alerts data.');
          }
        })
        .finally(() => {
          if (!cancelled && !silent) {
            setIsLoading(false);
          }
        });
    }

    runFetch(false);

    let intervalId: number | undefined;
    if (mode === 'auto') {
      intervalId = window.setInterval(() => runFetch(true), POLL_INTERVAL_SERVICES_ALERTS_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [mode, tabVisible, manualRefreshNonce]);

  const filteredAlerts = useMemo(() => {
    if (data === null) {
      return [];
    }
    if (envFilter === 'all') {
      return data.alerts;
    }
    return data.alerts.filter((a) => inferAlarmEnvironment(a) === envFilter);
  }, [data, envFilter]);

  return (
    <div className="alerts-tab">
      <h2>Active Alerts</h2>
      <p className="tab-lead">
        Combined CloudWatch alarms (all environments). Use the filter to narrow by inferred environment from alarm
        names and dimensions (heuristic).
      </p>

      {isLoading && <p className="placeholder">Loading alerts data...</p>}
      {!isLoading && error !== null && <p className="placeholder">{error}</p>}
      {!isLoading && error === null && data !== null && (
        <>
          <p className="fetched-at">Last updated {formatWhen(data.fetchedAt)}</p>

          <div className="alerts-toolbar" role="group" aria-label="Filter alarms by environment">
            <span className="alerts-toolbar-label">Show:</span>
            {(['all', 'dev', 'staging', 'prod'] as const).map((key) => (
              <button
                key={key}
                type="button"
                className={`filter-chip ${envFilter === key ? 'filter-chip-active' : ''}`}
                onClick={(): void => setEnvFilter(key)}
              >
                {key === 'all' ? 'All' : key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>

          {filteredAlerts.length === 0 ? (
            <p className="empty-state" role="status">
              {data.alerts.length === 0
                ? 'No active alarms in ALARM state.'
                : 'No alarms match this filter (try All, or check names / dimensions).'}
            </p>
          ) : (
            <ul className="alerts-list">
              {filteredAlerts.map((alarm) => (
                <li key={`${alarm.alarmName}-${alarm.stateUpdatedAt}`} className="alert-card">
                  <div className="alert-card-header">
                    <Badge variant={severityVariant(alarm.severity)}>{alarm.severity}</Badge>
                    <span className="alert-service">{alarmContextLabel(alarm)}</span>
                  </div>
                  <h3 className="alert-name">{alarm.alarmName}</h3>
                  {alarm.alarmDescription !== undefined && alarm.alarmDescription !== '' && (
                    <p className="alert-desc">{alarm.alarmDescription}</p>
                  )}
                  <p className="alert-reason">{alarm.stateReason}</p>
                  <p className="alert-updated">Updated {formatWhen(alarm.stateUpdatedAt)}</p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
